import React from "react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Modal } from "@tenforce/toolbox-modal-react"
import { Checkbox } from "@tenforce/product-fields"
import { ColorName } from "@tenforce/toolbox-default-style"

import { useAppSelector, useAppDispatch } from "../../../hooks"

import { setPrintOptions, hideDialogue, showPrintView } from "../slice"
import { Labels } from "../../../util/Translations"
import { IPrintOptions } from "../index"

export default function PrintDialogue(): JSX.Element {
    const { itemId, isShown, popupOptions, printOptions } = useAppSelector(
        (state) => state.printView,
    )
    const {
        Print,
        PrintOptions,
        PrintDescription,
        Cancel,
        Attachments,
        Comments,
        History,
    } = Labels

    const dispatch = useAppDispatch()

    const closeDialogue = (): void => {
        dispatch(hideDialogue())
    }

    const showPrintPreview = (): void => {
        dispatch(hideDialogue())
        showPrintView(itemId, printOptions)
    }

    const changePrintOptions = (newPrintOptions: IPrintOptions): void => {
        dispatch(setPrintOptions(newPrintOptions))
    }

    return (
        <>
            {itemId > 0 && (
                <Modal
                    isShown={isShown}
                    containerProps={{
                        id: "printDialogue",
                        isCenterFixedPositioned: true,
                        blockOverlayInteraction: true,
                        onClose: closeDialogue,
                    }}
                    contentProps={{
                        extraClasses: "tw-w-1/3 tw-mx-auto bg-default",
                    }}
                >
                    <div className="tw-p-4">
                        <h3>{PrintOptions}</h3>
                    </div>
                    <div className="separator-line is-dotted is-dimmed tw-pb-2"></div>

                    {!!PrintDescription && (
                        <div className="tw-px-4 tw-py-2 tw-text-placeholder">
                            {PrintDescription}
                        </div>
                    )}

                    {popupOptions.canShowAttachments && (
                        <div className="tw-px-2">
                            <Checkbox
                                title={Attachments}
                                value={printOptions.shouldPrintAttachments}
                                fieldId="attachments"
                                checkColor={ColorName.Jade}
                                onSave={async (value): Promise<void> =>
                                    changePrintOptions({
                                        ...printOptions,
                                        shouldPrintAttachments: value,
                                    })
                                }
                            />
                        </div>
                    )}
                    {popupOptions.canShowComments && (
                        <div className="tw-px-2">
                            <Checkbox
                                title={Comments}
                                value={printOptions.shouldPrintComments}
                                fieldId="comments"
                                checkColor={ColorName.Jade}
                                onSave={async (value): Promise<void> =>
                                    changePrintOptions({
                                        ...printOptions,
                                        shouldPrintComments: value,
                                    })
                                }
                            />
                        </div>
                    )}
                    {popupOptions.canShowHistory && (
                        <div className="tw-px-2">
                            <Checkbox
                                title={History}
                                value={printOptions.shouldPrintHistory}
                                fieldId="history"
                                checkColor={ColorName.Jade}
                                onSave={async (value): Promise<void> =>
                                    changePrintOptions({
                                        ...printOptions,
                                        shouldPrintHistory: value,
                                    })
                                }
                            />
                        </div>
                    )}

                    <div className="separator-line is-dotted is-dimmed tw-pt-2"></div>
                    <div className="tw-p-2">
                        <Action
                            actionType={ActionType.button}
                            onClick={showPrintPreview}
                            data-ft-component="printButton"
                        >
                            {Print}
                        </Action>
                        <Action
                            actionType={ActionType.button}
                            isTransparent={true}
                            isSecondary={true}
                            onClick={closeDialogue}
                            data-ft-component="cancelButton"
                        >
                            {Cancel}
                        </Action>
                    </div>
                </Modal>
            )}
        </>
    )
}
